import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { TrendingUp, Users, Share2 } from 'lucide-react';

interface CalculatorCardProps {
  title: string;
  description: string;
  link: string;
  icon: React.ElementType;
}

const SocialMediaInfluencersPage = () => {
  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/social-media-influencers`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Social Media & Influencer Marketing Calculators | Free Tools</title>
        <meta name="description" content="Optimize your social media and influencer marketing with our free calculators. Measure ROI, organic reach, engagement rates & more." />
        <meta name="keywords" content="social media ROI calculator, influencer marketing ROI, organic reach calculator, social media metrics" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Social Media & Influencer Marketing Calculators" />
        <meta property="og:description" content="Optimize your social media and influencer campaigns with our free calculators. Track ROI, reach, and engagement." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Social Media & Influencer Marketing Calculators" />
        <meta property="twitter:description" content="Optimize your social media and influencer campaigns with our free calculators. Track ROI, reach, and engagement." />
        <meta property="twitter:image" content={logoUrl} />
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6">Social Media & Influencer Marketing</h1>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4">Performance Calculators</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <CalculatorCard 
            title="Social Media ROI" 
            description="Measure the return on investment for your social media campaigns" 
            link="/calculators/social-media-roi" 
            icon={TrendingUp}
          />
          <CalculatorCard 
            title="Influencer Marketing ROI" 
            description="Calculate the return on investment for influencer marketing campaigns" 
            link="/calculators/influencer-marketing-roi" 
            icon={Users}
          />
          <CalculatorCard 
            title="Organic Social Media Reach" 
            description="Calculate the organic reach of your social media posts" 
            link="/calculators/organic-social-media-reach" 
            icon={Share2}
          />
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4">Understanding Social Media & Influencer Marketing Metrics</h2>
        <p className="text-gray-600 mb-4">
          Measuring the success of your social media and influencer marketing efforts is crucial for optimizing your strategy and maximizing ROI. Use these calculators to track and improve your performance across platforms.
        </p>
      </section>
    </div>
  );
};

const CalculatorCard: React.FC<CalculatorCardProps> = ({ title, description, link, icon: Icon }) => (
  <Link to={link} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
    <Icon className="w-8 h-8 text-indigo-600 mb-4" />
    <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
    <p className="text-gray-600 mb-4">{description}</p>
    <span className="text-indigo-600 font-medium">Use Calculator</span>
  </Link>
);

export default SocialMediaInfluencersPage;
